import { Box, Typography, Button, Paper, Grid, Card, CardContent, Container } from '@mui/material';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { useUserProfile } from '../hooks/useUserProfile';
import WelcomeDialog from './WelcomeDialog';
import CreateIcon from '@mui/icons-material/Create';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShareIcon from '@mui/icons-material/Share';
import SecurityIcon from '@mui/icons-material/Security';
import CloudIcon from '@mui/icons-material/Cloud';
import PersonIcon from '@mui/icons-material/Person';

const Home = () => {
  const [user] = useAuthState(auth);
  const { userProfile, updateProfile } = useUserProfile();
  const navigate = useNavigate();

  const handleWelcomeUpdate = (updates) => {
    updateProfile(updates);
  };

  const features = [
    {
      icon: <CreateIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Dynamic Form Creation',
      description: 'Create custom forms with drag-and-drop functionality and real-time validation.'
    },
    {
      icon: <DashboardIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Submission Management',
      description: 'Track, analyze, and export form submissions with powerful analytics.'
    },
    {
      icon: <ShareIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Easy Sharing',
      description: 'Share forms instantly with unique links and embed codes.'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Secure Authentication',
      description: 'Google & GitHub OAuth integration for secure user management.'
    },
    {
      icon: <CloudIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'Cloud Storage',
      description: 'Reliable Firestore database for all your form data and submissions.'
    },
    {
      icon: <PersonIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: 'User Profiles',
      description: 'Personalized profiles with public pages and social features.'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Hero Section */}
      <Box 
        sx={{ 
          textAlign: 'center', 
          mb: 8,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 3,
          py: 8,
          px: 4
        }}
      >
        <Typography 
          variant="h2" 
          component="h1" 
          gutterBottom
          sx={{ 
            fontWeight: 'bold',
            mb: 2,
            fontSize: { xs: '2.5rem', md: '3.5rem' }
          }}
        >
          FormPulse
        </Typography>
        <Typography 
          variant="h5" 
          sx={{ 
            mb: 4, 
            opacity: 0.9,
            fontSize: { xs: '1.2rem', md: '1.5rem' }
          }}
        >
          The Modern Form Management Platform
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            mb: 5, 
            fontSize: '1.1rem',
            maxWidth: 600,
            mx: 'auto'
          }}
        >
          Create beautiful forms, collect responses, and analyze data with our powerful, 
          user-friendly platform. Join thousands of users who trust FormPulse.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          {user ? (
            <>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/admin')}
                sx={{ 
                  bgcolor: 'white', 
                  color: 'primary.main',
                  '&:hover': { bgcolor: 'grey.100' },
                  px: 4,
                  py: 1.5
                }}
              >
                Create Your First Form
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/my-submissions')}
                sx={{ 
                  borderColor: 'white', 
                  color: 'white',
                  '&:hover': { borderColor: 'grey.300', bgcolor: 'rgba(255,255,255,0.1)' },
                  px: 4,
                  py: 1.5
                }}
              >
                Go to Dashboard
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/login')}
              sx={{ 
                bgcolor: 'white', 
                color: 'primary.main',
                '&:hover': { bgcolor: 'grey.100' },
                px: 4,
                py: 1.5
              }}
            >
              Get Started Free
            </Button>
          )}
        </Box>
      </Box>

      {/* Features Section */}
      <Box sx={{ mb: 8 }}>
        <Typography 
          variant="h4" 
          component="h2" 
          textAlign="center" 
          gutterBottom
          sx={{ mb: 6, fontWeight: 'bold' }}
        >
          Why Choose FormPulse?
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} lg={4} key={index}>
              <Card 
                sx={{ 
                  height: '100%',
                  textAlign: 'center',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: 4
                  }
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

        <Paper 
          elevation={3} 
          sx={{ 
            p: 6, 
            textAlign: 'center',
            background: 'linear-gradient(45deg, #f3f4f6 0%, #e5e7eb 100%)',
            borderRadius: 3
          }}
        >
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Be Among the First to Shape FormPulse!
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
            We're just getting started—join now and help us build the future of form management. Your feedback matters!
          </Typography>
          {!user && (
            <Button
          variant="contained"
          size="large"
          onClick={() => navigate('/auth')}
          sx={{ 
            px: 5, 
            py: 1.7, 
            fontWeight: 'bold', 
            fontSize: '1.1rem',
            bgcolor: 'primary.main',
            color: 'white',
            boxShadow: 3,
            '&:hover': { bgcolor: 'primary.dark' }
          }}
            >
          Get Started – Join Early!
            </Button>
          )}
        </Paper>
        
        {/* Welcome Dialog for First-Time Users */}
      <WelcomeDialog
        open={userProfile?.isFirstLogin === true}
        userProfile={userProfile}
        onClose={() => updateProfile({ isFirstLogin: false })}
        onUpdate={handleWelcomeUpdate}
      />
    </Container>
  );
};

export default Home;